import * as dagre from '@dagrejs/dagre';
import { message } from '@repo/ui';
import { type Edge, type Node } from '@xyflow/react';
import { NodeType } from 'automations/data/nodeTypes';

const getExecutionOrder = (node: Node): number => (
  Number.isSafeInteger(node.data.executionOrder) ? node.data.executionOrder as number : Number.MAX_SAFE_INTEGER
);

const getSortedChildren = (nodeId: string, nodes: Node[], edges: Edge[]): Node[] => edges
  .filter((edge) => edge.source === nodeId)
  .map((edge) => nodes.find((n) => n.id === edge.target))
  .filter((node): node is Node => node !== undefined)
  .sort((a, b) => getExecutionOrder(a) - getExecutionOrder(b));

function traverseInorder(
  nodes: Node[],
  edges: Edge[],
  nodeId: string,
  visited = new Set<string>(),
  result: Node[] = [],
): Node[] {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node || visited.has(nodeId)) return result;

  const children = getSortedChildren(nodeId, nodes, edges);
  const midPoint = Math.floor(children.length / 2);

  // Process left subtree
  children.slice(0, midPoint).forEach((child) => traverseInorder(nodes, edges, child.id, visited, result));

  // Process current node
  visited.add(nodeId);
  result.push(node);

  // Process right subtree
  children.slice(midPoint).forEach((child) => traverseInorder(nodes, edges, child.id, visited, result));

  return result;
}

export function formatAutomationTree(nodes: Node[], edges: Edge[]): Node[] {
  const rootNode = nodes.find((node) => (
    node.type === NodeType.trigger || node.data?.serverNodeType === NodeType.trigger
  ));
  if (!rootNode) {
    message.error('Something went wrong, Contact Support');
    return [];
  }

  // Get sorted nodes through inorder traversal
  const visited = new Set<string>();

  /**
   * Traverse the tree in-order to ensure nodes are processed in the correct execution order.
   * Since Dagre uses the node order we provide for layout calculations, we need to:
   * 1. Sort root nodes by execution order
   * 2. Traverse each root's subtree in-order, respecting child execution orders
   * 3. Add any remaining unvisited nodes at the end
   * This ensures the final layout properly reflects the logical flow of the automation.
   * @returns {Node[]} Sorted array of nodes in the correct execution order
   */
  const sortedNodes = traverseInorder(nodes, edges, rootNode.id, visited);

  nodes.forEach((node) => {
    if (!visited.has(node.id)) sortedNodes.push(node);
  });

  // Configure and create dagre graph
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setGraph({
    rankdir: 'TB',
    nodesep: 50,
    ranksep: 80,
    edgesep: 30,
    marginx: 20,
    marginy: 20,
    acyclicer: 'greedy',
    ranker: 'tight-tree',
  });
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Add nodes and edges to dagre
  sortedNodes.forEach((node) => {
    dagreGraph.setNode(`node-${node.id}`, {
      label: node.data.label as string ?? '',
      width: 360,
      height: 60,
    });
  });

  const orderedEdges = edges.sort((a, b) => {
    const aNode = nodes.find((node) => node.id === a.target);
    const bNode = nodes.find((node) => node.id === b.target);
    if (!aNode || !bNode) return 0;
    return getExecutionOrder(aNode) - getExecutionOrder(bNode);
  });

  orderedEdges.forEach((edge) => {
    dagreGraph.setEdge(`node-${edge.source}`, `node-${edge.target}`, {
      label: '',
      width: 1,
      minlen: 1,
    });
  });

  // Layout and position nodes
  dagre.layout(dagreGraph);

  return nodes.map((node) => {
    const {
      x, width, y, height,
    } = dagreGraph.node(`node-${node.id}`);
    return {
      ...node,
      position: {
        x: x - width / 2,
        y: y - height / 2,
      },
    } as Node;
  });
}
